import React, { useState, useEffect, useCallback } from "react";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../../components/Breadcrumb";
import ProjectCard from "../../components/Projects/ProjectCard";
import api from "../../services/api";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";

const UnitsProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const storedUser = JSON.parse(localStorage.getItem("user"));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [totalRows, setTotalRows] = useState(0);

  // Fetch projects with pagination and server-side search
  const fetchProjects = useCallback(async (page, limit, search = "") => {
    setLoading(true);
    try {
      let endpoint;
      if (storedUser.type === "admin") {
        // Build query params for paginated request
        let queryParams = `page=${page}&per_page=${limit}`;
        // Use search param - searches project name and developer name automatically
        if (search) {
          queryParams += `&search=${encodeURIComponent(search)}`;
        }
        endpoint = `/projects?${queryParams}`;
      } else {
        endpoint = "/projects/developer/projects";
      }

      const { data } = await api.get(endpoint);
      if (data.status) {
        const projectData = Array.isArray(data.response?.data) ? data.response.data : [];
        setProjects(projectData);

        // Set total rows for pagination
        if (data.response?.total !== undefined) {
          setTotalRows(data.response.total);
        } else {
          setTotalRows(projectData.length);
        }
      } else {
        console.error("Error fetching projects:", data.message);
        setProjects([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [storedUser.type]);

  // Fetch on mount and when pagination changes
  useEffect(() => {
    fetchProjects(currentPage, perPage, searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Trigger search when searchQuery changes (with debounce effect)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 on search
      fetchProjects(1, perPage, searchQuery);
    }, 400);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Calculate total pages
  const totalPages = Math.ceil(totalRows / perPage);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle per page change
  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Projects" />
      <p>Choose a project to access its units.</p>

      {/* Search and Add Unit Button */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <div style={{ minWidth: "250px" }}>
            <input
              type="text"
              placeholder="Search by project name..."
              className="form-control"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          {/* Items per page selector */}
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="perPageSelect" className="form-label mb-0 text-nowrap">
              Per page:
            </label>
            <select
              id="perPageSelect"
              className="form-select form-select-sm"
              value={perPage}
              onChange={handlePerPageChange}
              style={{ width: "auto" }}
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
              <option value={96}>96</option>
            </select>
          </div>
        </div>
        <div>
          <Link
            to="/add-unit"
            className="btn btn-primary btn-sm d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="icon text-sm" />
            Add New Unit
          </Link>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="row gy-4">
        {loading ? (
          <div className="col-12 d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : projects.length > 0 ? (
          projects.map((project) => (
            <div key={project.id} className="col-xxl-3 col-sm-6">
              <ProjectCard project={project} />
            </div>
          ))
        ) : (
          <div className="col-12 text-center">
            <p>No projects found.</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
          {/* Showing info */}
          <div className="text-muted">
            Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalRows)} of {totalRows} projects
          </div>

          {/* Pagination buttons */}
          <nav aria-label="Projects pagination">
            <ul className="pagination mb-0">
              {/* First page */}
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                >
                  <Icon icon="mdi:chevron-double-left" />
                </button>
              </li>
              {/* Previous page */}
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <Icon icon="mdi:chevron-left" />
                </button>
              </li>

              {/* Page numbers */}
              {getPageNumbers().map((page) => (
                <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                </li>
              ))}

              {/* Next page */}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <Icon icon="mdi:chevron-right" />
                </button>
              </li>
              {/* Last page */}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last page"
                >
                  <Icon icon="mdi:chevron-double-right" />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </MasterLayout>
  );
};

export default UnitsProjectsPage;